import React, { useState } from 'react';
import { useCRM } from '../context/CRMContext';
import { upsertCompany, updateCompany, deleteCompany } from '../firebase/firestore';

const PLANS = [
  { id: 'starter',    label: 'Starter',    price: '$29/mo',  color: '#10b981' },
  { id: 'pro',        label: 'Pro',        price: '$79/mo',  color: '#3b82f6' },
  { id: 'enterprise', label: 'Enterprise', price: 'Custom',  color: '#7c3aed' },
];

const ALL_FEATURES = [
  { key: 'webForms',     label: 'Web Forms',     icon: '📝', desc: 'Build embeddable lead capture forms' },
  { key: 'assessments',  label: 'Assessments',   icon: '📊', desc: 'Send psychometric tests to leads' },
  { key: 'campaigns',    label: 'Campaigns',     icon: '📣', desc: 'Email & WhatsApp campaign management' },
  { key: 'integrations', label: 'Integrations',  icon: '🔗', desc: 'Facebook, Google Sheets, Twilio etc.' },
  { key: 'activityLog',  label: 'Activity Log',  icon: '📋', desc: 'Full audit trail of all actions' },
  { key: 'roundRobin',   label: 'Round Robin',   icon: '🔄', desc: 'Automated lead assignment to counselors' },
];

const DEFAULT_THEME = { appName: 'Clarion CRM', primary: '#7c3aed', secondary: '#3b82f6', primaryText: '' };
const DEFAULT_FEATURES = { webForms: true, assessments: true, campaigns: true, integrations: true, activityLog: true, roundRobin: true };

export default function SuperAdmin() {
  const { allCompanies, currentUser } = useCRM();

  const [tab, setTab]             = useState('companies');
  const [showModal, setShowModal] = useState(false);
  const [editCo, setEditCo]       = useState(null);
  const [saving, setSaving]       = useState(false);

  const [form, setForm] = useState({
    id: '', name: '', adminEmail: '', plan: 'pro', active: true,
    logoUrl: '', theme: { ...DEFAULT_THEME },
    features: { ...DEFAULT_FEATURES },
  });

  if (currentUser?.role !== 'superadmin') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>Super Admin only</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>You don't have access to this section.</div>
        </div>
      </div>
    );
  }

  const setF = (k, v)            => setForm(p => ({ ...p, [k]: v }));
  const setTheme = (k, v)        => setForm(p => ({ ...p, theme: { ...p.theme, [k]: v } }));
  const setFeature = (k, v)      => setForm(p => ({ ...p, features: { ...p.features, [k]: v } }));

  const openNew = () => {
    setEditCo(null);
    setForm({ id: '', name: '', adminEmail: '', plan: 'pro', active: true, logoUrl: '', theme: { ...DEFAULT_THEME }, features: { ...DEFAULT_FEATURES } });
    setShowModal(true);
  };

  const openEdit = (co) => {
    setEditCo(co);
    setForm({
      id: co.id,
      name: co.name || '',
      adminEmail: co.adminEmail || '',
      plan: co.plan || 'pro',
      active: co.active !== false,
      logoUrl: co.logoUrl || '',
      theme: { ...DEFAULT_THEME, ...co.theme },
      features: { ...DEFAULT_FEATURES, ...co.features },
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.id.trim() || !form.name.trim()) return;
    setSaving(true);
    try {
      if (editCo) {
        await updateCompany(form.id, {
          name: form.name, adminEmail: form.adminEmail,
          plan: form.plan, active: form.active,
          logoUrl: form.logoUrl, theme: form.theme, features: form.features,
        });
      } else {
        await upsertCompany(form.id, {
          name: form.name, adminEmail: form.adminEmail,
          plan: form.plan, active: form.active,
          logoUrl: form.logoUrl, theme: form.theme, features: form.features,
          slug: form.id,
        });
      }
      setShowModal(false);
    } catch (err) {
      console.error('Save company error', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (co) => {
    if (!window.confirm(`Delete company "${co.name}"? This cannot be undone.`)) return;
    await deleteCompany(co.id);
  };

  const toggleActive = (co) =>
    updateCompany(co.id, { active: !co.active });

  const toggleFeature = (co, key) =>
    updateCompany(co.id, { features: { ...DEFAULT_FEATURES, ...co.features, [key]: !(co.features?.[key] ?? true) } });

  // Stats
  const totalCompanies = allCompanies.length;
  const activeCompanies = allCompanies.filter(c => c.active !== false).length;
  const planCounts = PLANS.reduce((acc, p) => { acc[p.id] = allCompanies.filter(c => c.plan === p.id).length; return acc; }, {});

  const StatCard = ({ label, value, color, icon }) => (
    <div style={{ flex: 1, background: `${color}10`, border: `1px solid ${color}25`, borderRadius: 14, padding: '16px 20px', display: 'flex', gap: 14, alignItems: 'center' }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>⚡</div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a' }}>Super Admin</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Manage companies, features, and billing</div>
            </div>
          </div>
        </div>
        <button className="btn-primary" onClick={openNew} style={{ fontSize: 13, padding: '8px 18px', background: 'linear-gradient(135deg,#f59e0b,#ef4444)' }}>+ Add Company</button>
      </div>

      {/* Stats */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(0,0,0,0.05)', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <StatCard label="Total Companies" value={totalCompanies}  color="#f59e0b" icon="🏢" />
          <StatCard label="Active"           value={activeCompanies} color="#10b981" icon="✅" />
          <StatCard label="Pro Plans"        value={planCounts.pro || 0}        color="#3b82f6" icon="⭐" />
          <StatCard label="Enterprise"       value={planCounts.enterprise || 0} color="#7c3aed" icon="🚀" />
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ padding: '12px 24px 0', flexShrink: 0 }}>
        <div className="tab-bar" style={{ width: 'fit-content' }}>
          {['companies', 'features'].map(t => (
            <div key={t} className={`tab-item${tab === t ? ' active' : ''}`} onClick={() => setTab(t)} style={{ textTransform: 'capitalize' }}>{t}</div>
          ))}
        </div>
      </div>

      {/* ── Companies list ──────────────────────────────────────────────────────── */}
      {tab === 'companies' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {allCompanies.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🏢</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>No companies yet</div>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Add your first client company to get started</div>
              <button className="btn-primary" onClick={openNew} style={{ fontSize: 13, background: 'linear-gradient(135deg,#f59e0b,#ef4444)' }}>+ Add Company</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {allCompanies.map(co => {
                const plan = PLANS.find(p => p.id === co.plan) || PLANS[1];
                const featureCount = Object.values(co.features || DEFAULT_FEATURES).filter(Boolean).length;
                return (
                  <div key={co.id} className="glass-card" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                    {/* Logo / initials */}
                    {co.logoUrl ? (
                      <img src={co.logoUrl} alt={co.name} style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain', flexShrink: 0 }} />
                    ) : (
                      <div style={{
                        width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                        background: `linear-gradient(135deg, ${co.theme?.primary || '#7c3aed'}, ${co.theme?.secondary || '#3b82f6'})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 18, fontWeight: 800, color: '#fff',
                      }}>
                        {(co.name || 'C').slice(0, 1).toUpperCase()}
                      </div>
                    )}

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{co.name}</div>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: `${plan.color}15`, color: plan.color, border: `1px solid ${plan.color}30` }}>{plan.label}</span>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: co.active !== false ? 'rgba(16,185,129,0.15)' : 'rgba(148,163,184,0.15)', color: co.active !== false ? '#10b981' : '#94a3b8', border: `1px solid ${co.active !== false ? 'rgba(16,185,129,0.3)' : 'rgba(148,163,184,0.3)'}` }}>
                          {co.active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#64748b', flexWrap: 'wrap' }}>
                        <span>🆔 {co.id}</span>
                        {co.adminEmail && <span>📧 {co.adminEmail}</span>}
                        <span>🎨 {co.theme?.primary || '#7c3aed'}</span>
                        <span>⚙️ {featureCount}/{ALL_FEATURES.length} features</span>
                      </div>
                    </div>

                    {/* Colour swatch */}
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: co.theme?.primary || '#7c3aed', flexShrink: 0 }} title={co.theme?.primary} />

                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button
                        onClick={() => toggleActive(co)}
                        style={{ background: co.active !== false ? 'rgba(16,185,129,0.1)' : 'rgba(148,163,184,0.1)', border: `1px solid ${co.active !== false ? 'rgba(16,185,129,0.25)' : 'rgba(148,163,184,0.2)'}`, color: co.active !== false ? '#10b981' : '#94a3b8', borderRadius: 7, padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                      >
                        {co.active !== false ? 'Disable' : 'Enable'}
                      </button>
                      <button onClick={() => openEdit(co)} style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', color: '#3b82f6', borderRadius: 7, padding: '5px 10px', fontSize: 11, cursor: 'pointer' }}>Edit</button>
                      <button onClick={() => handleDelete(co)} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', borderRadius: 7, padding: '5px 8px', fontSize: 11, cursor: 'pointer' }}>✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Feature flags grid ──────────────────────────────────────────────────── */}
      {tab === 'features' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
            Enable or disable features per company. Changes apply immediately.
          </div>
          {allCompanies.map(co => (
            <div key={co.id} className="glass-card" style={{ padding: '16px 20px', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg,${co.theme?.primary||'#7c3aed'},${co.theme?.secondary||'#3b82f6'})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                  {(co.name||'C').slice(0,1).toUpperCase()}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{co.name}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>({co.id})</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: 8 }}>
                {ALL_FEATURES.map(feat => {
                  const enabled = co.features?.[feat.key] !== false;
                  return (
                    <div
                      key={feat.key}
                      onClick={() => toggleFeature(co, feat.key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                        borderRadius: 10, cursor: 'pointer',
                        background: enabled ? 'rgba(16,185,129,0.08)' : 'rgba(0,0,0,0.03)',
                        border: `1px solid ${enabled ? 'rgba(16,185,129,0.2)' : 'rgba(0,0,0,0.06)'}`,
                        transition: 'all 0.15s',
                      }}
                    >
                      <span style={{ fontSize: 18 }}>{feat.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: enabled ? '#0f172a' : '#94a3b8' }}>{feat.label}</div>
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>{feat.desc}</div>
                      </div>
                      <div style={{
                        width: 32, height: 18, borderRadius: 9,
                        background: enabled ? '#10b981' : '#cbd5e1',
                        position: 'relative', flexShrink: 0,
                        transition: 'background 0.2s',
                      }}>
                        <div style={{
                          width: 12, height: 12, borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: 3,
                          left: enabled ? 17 : 3,
                          transition: 'left 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Add / Edit Company Modal ─────────────────────────────────────────────── */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box modal-wide" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{editCo ? 'Edit Company' : 'New Company'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            {/* Basic Info */}
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>Basic Info</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Company ID * <span style={{ fontSize: 10, color: '#94a3b8' }}>(slug, e.g. abc-edu)</span></label>
                <input className="crm-input" placeholder="abc-education" value={form.id} onChange={e => setF('id', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,''))} disabled={!!editCo} style={editCo ? { opacity: 0.6 } : {}} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Company Name *</label>
                <input className="crm-input" placeholder="ABC Education Pvt Ltd" value={form.name} onChange={e => setF('name', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Admin Email</label>
                <input className="crm-input" type="email" placeholder="admin@abc.com" value={form.adminEmail} onChange={e => setF('adminEmail', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Plan</label>
                <select className="crm-select" style={{ width: '100%' }} value={form.plan} onChange={e => setF('plan', e.target.value)}>
                  {PLANS.map(p => <option key={p.id} value={p.id}>{p.label} — {p.price}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Logo URL</label>
                <input className="crm-input" placeholder="https://..." value={form.logoUrl} onChange={e => setF('logoUrl', e.target.value)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" className="crm-checkbox" id="co-active" checked={form.active} onChange={e => setF('active', e.target.checked)} />
                <label htmlFor="co-active" style={{ fontSize: 13, color: '#475569', cursor: 'pointer' }}>Company active</label>
              </div>
            </div>

            {/* Branding */}
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>Branding</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>App Name (shown in sidebar)</label>
                <input className="crm-input" placeholder="Clarion CRM" value={form.theme.appName} onChange={e => setTheme('appName', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Primary Colour</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={form.theme.primary} onChange={e => setTheme('primary', e.target.value)} style={{ width: 40, height: 38, padding: 2, borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }} />
                  <input className="crm-input" value={form.theme.primary} onChange={e => setTheme('primary', e.target.value)} style={{ flex: 1, fontFamily: 'monospace' }} placeholder="#7c3aed" />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Secondary Colour</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={form.theme.secondary} onChange={e => setTheme('secondary', e.target.value)} style={{ width: 40, height: 38, padding: 2, borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }} />
                  <input className="crm-input" value={form.theme.secondary} onChange={e => setTheme('secondary', e.target.value)} style={{ flex: 1, fontFamily: 'monospace' }} placeholder="#3b82f6" />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Preview</label>
                <div style={{ height: 38, borderRadius: 9, background: `linear-gradient(135deg,${form.theme.primary},${form.theme.secondary})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700 }}>
                  {form.theme.appName || 'Preview'}
                </div>
              </div>
            </div>

            {/* Feature flags */}
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>Features</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 24 }}>
              {ALL_FEATURES.map(feat => {
                const enabled = form.features[feat.key] !== false;
                return (
                  <div
                    key={feat.key}
                    onClick={() => setFeature(feat.key, !enabled)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                      borderRadius: 10, cursor: 'pointer',
                      background: enabled ? 'rgba(16,185,129,0.08)' : 'rgba(0,0,0,0.03)',
                      border: `1px solid ${enabled ? 'rgba(16,185,129,0.2)' : 'rgba(0,0,0,0.06)'}`,
                      transition: 'all 0.15s',
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{feat.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{feat.label}</div>
                      <div style={{ fontSize: 10, color: '#64748b' }}>{feat.desc}</div>
                    </div>
                    <div style={{
                      width: 34, height: 20, borderRadius: 10,
                      background: enabled ? '#10b981' : '#cbd5e1',
                      position: 'relative', flexShrink: 0, transition: 'background 0.2s',
                    }}>
                      <div style={{
                        width: 14, height: 14, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 3, left: enabled ? 17 : 3, transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={saving || !form.id.trim() || !form.name.trim()}
                style={{ background: 'linear-gradient(135deg,#f59e0b,#ef4444)' }}
              >
                {saving ? 'Saving…' : editCo ? 'Save Changes' : 'Create Company'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
